import React from "react";
import {
  Activity, AlertTriangle, Brain, Bug, Building2, CalendarClock, Crosshair, ExternalLink, FileCode2, Flame, Loader2, Package, Search, ShieldAlert, ShieldCheck, Sparkles,
} from "lucide-react";
import * as api from "../api/cyber";
import {
  card, th, td, btn, SEV, BAND, Pill, Kpi, Provenance, useAsync, Loading, Err, n, money,
} from "./cyberShared";
import { Sbom, Authorisation, AiSystems, Trends, RiskMap } from "./CyberTabs";
import { RunwayTimeline } from "../features/cyber/charts";

/**
 * Cyber posture.
 *
 * Built on data the estate already held and nothing rendered: end_of_support_date
 * was referenced by no screen in the application, and the FedRAMP and supply-chain
 * columns were likewise unused. Vulnerability and dependency findings are
 * generated in the exact shape of NVD / CISA KEV / GHSA, and every screen says so.
 */

const TABS = [
  { id: "posture", label: "Posture", icon: ShieldAlert },
  { id: "riskmap", label: "Risk Map", icon: Crosshair },
  { id: "vulnerabilities", label: "COTS Vulnerabilities", icon: Bug },
  { id: "lifecycle", label: "Out of Support", icon: CalendarClock },
  { id: "opensource", label: "Open Source", icon: Package },
  { id: "supply", label: "Supply Chain", icon: Building2 },
  { id: "sbom", label: "SBOM", icon: FileCode2 },
  { id: "authorisation", label: "ATO & POA&M", icon: ShieldCheck },
  { id: "ai", label: "AI Systems", icon: Brain },
  { id: "trends", label: "Trends", icon: Activity },
];


/**
 * Renders the analyzer's briefing.
 *
 * The model returns markdown, and a <pre> showed it raw — "## Bottom line" and
 * "**overdue**" as literal characters in what is meant to read as a written
 * brief. Only headings, bold and bullets ever appear, so they are turned into
 * elements directly rather than pulling in a markdown parser.
 */
function Brief({ text }) {
  const bold = (line, key) =>
    String(line).split(/\*\*(.+?)\*\*/g).map((part, i) =>
      i % 2 === 1
        ? <strong key={`${key}-${i}`} style={{ fontWeight: 700, color: "var(--cares-text-1)" }}>{part}</strong>
        : <span key={`${key}-${i}`}>{part}</span>);

  return (
    <div style={{ fontSize: 13, lineHeight: 1.65, color: "var(--cares-text-2)" }}>
      {String(text || "").split("\n").map((raw, i) => {
        const line = raw.trim();
        if (!line) return <div key={i} style={{ height: 9 }} />;
        if (line.startsWith("## ")) {
          return (
            <h4 key={i} style={{ fontSize: 13.5, fontWeight: 700, color: "var(--cares-text-1)",
                                 margin: "16px 0 6px", letterSpacing: "-.005em" }}>
              {line.slice(3)}
            </h4>
          );
        }
        if (line.startsWith("# ")) {
          return (
            <h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: "var(--cares-text-1)",
                                 margin: "14px 0 8px" }}>{line.slice(2)}</h3>
          );
        }
        const numbered = line.match(/^(\d+)\.\s+(.*)$/);
        const bullet = line.startsWith("- ") || line.startsWith("* ");
        if (numbered || bullet) {
          return (
            <div key={i} style={{ display: "flex", gap: 9, margin: "4px 0 4px 4px" }}>
              <span style={{ color: "var(--cares-text-note)", flexShrink: 0, fontWeight: 600,
                             minWidth: numbered ? 16 : 8 }}>
                {numbered ? `${numbered[1]}.` : "\u2022"}
              </span>
              <span>{bold(numbered ? numbered[2] : line.slice(2), i)}</span>
            </div>
          );
        }
        return <p key={i} style={{ margin: "0 0 4px" }}>{bold(line, i)}</p>;
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ posture */
function Posture() {
  const { loading, data, error } = useAsync(() => api.getSummary(), []);
  const [analysis, setAnalysis] = React.useState(null);
  const [analysing, setAnalysing] = React.useState(false);
  const [analysisError, setAnalysisError] = React.useState("");

  const runAnalyzer = async () => {
    setAnalysing(true); setAnalysisError(""); setAnalysis(null);
    try {
      setAnalysis(await api.analyze({}));
    } catch (e) {
      setAnalysisError(e.message || String(e));
    } finally { setAnalysing(false); }
  };

  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;

  const { estate, vulnerabilities: v, openSource: o, lifecycle: l,
          compliance: c, riskBands, topRisks } = data;

  return (
    <div>
      <Provenance provenance={data.provenance} />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="Known exploited" value={v.knownExploited} icon={Flame}
             tone={v.knownExploited ? "var(--cares-orange)" : "var(--cares-green)"}
             sub={`${v.kevOverdue} past the BOD 22-01 deadline`} />
        <Kpi label="Devices exposed" value={n(v.exposedDevices)} icon={ShieldAlert}
             tone="var(--cares-orange)" sub={`of ${n(estate.devices)} in the estate`} />
        <Kpi label="Critical + high" value={v.critical + v.high} icon={Bug}
             sub={`${v.total} vulnerabilities total`} />
        <Kpi label="Losing support" value={l.endingWithin365Days} icon={CalendarClock}
             tone={l.endingWithin365Days ? "var(--cares-amber)" : "var(--cares-green)"}
             sub={l.nextExpiry ? `next on ${l.nextExpiry}` : "none within a year"} />
        <Kpi label="Open-source advisories" value={o.total} icon={Package}
             sub={`${o.critical + o.high} critical or high`} />
        <Kpi label="Not FedRAMP" value={c.notFedrampAuthorized} icon={Building2}
             tone={c.notFedrampAuthorized ? "var(--cares-amber)" : "var(--cares-green)"}
             sub={`${c.fedrampAuthorized} of ${estate.products} authorised`} />
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ ...card, padding: 14, flex: "1 1 260px", minWidth: 0 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 10px" }}>
            Products by exposure
          </h3>
          {["CRITICAL", "HIGH", "MODERATE", "LOW"].map((b) => {
            const count = riskBands[b] || 0;
            const pct = estate.products ? (count / estate.products) * 100 : 0;
            return (
              <div key={b} style={{ marginBottom: 7 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              fontSize: 11, color: "var(--cares-text-3)", marginBottom: 3 }}>
                  <span style={{ fontWeight: 600 }}>{b}</span><span>{count}</span>
                </div>
                <div style={{ height: 7, background: "var(--cares-surface-board)", borderRadius: 4 }}>
                  <div style={{ width: `${pct}%`, height: "100%", borderRadius: 4,
                                background: BAND[b] }} />
                </div>
              </div>
            );
          })}
          {c.supplyChainUnassessed > 0 && (
            <div style={{ marginTop: 12, padding: 9, borderRadius: 8,
                          background: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))", border: "1px solid color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))",
                          fontSize: 11, color: "var(--cares-amber)", display: "flex", gap: 7 }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                No supply-chain risk assessment exists for any of the{" "}
                {c.supplyChainUnassessed} products. That is missing coverage, not a
                clean result.
              </span>
            </div>
          )}
        </div>

        <div style={{ ...card, flex: "2 1 440px", overflow: "hidden" }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, padding: "13px 14px 9px" }}>
            Highest exposure
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Product</th><th style={th}>Score</th>
                <th style={th}>KEV</th><th style={th}>Crit</th>
                <th style={th}>Devices</th><th style={th}>Support ends</th>
              </tr></thead>
              <tbody>
                {topRisks.map((p) => (
                  <tr key={p.productId}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>{p.product}</div>
                      <div style={{ fontSize: 10.5, color: "var(--cares-text-note)" }}>{p.manufacturer}</div>
                    </td>
                    <td style={td}>
                      <span style={{ fontWeight: 700, color: BAND[p.exposure.band] }}>
                        {p.exposure.score}
                      </span>
                      <div style={{ fontSize: 9.5, color: "var(--cares-text-note)" }}>{p.exposure.band}</div>
                    </td>
                    <td style={{ ...td, color: p.counts.kev ? "var(--cares-orange)" : "var(--cares-text-note)",
                                 fontWeight: p.counts.kev ? 700 : 400 }}>
                      {p.counts.kev || "—"}
                    </td>
                    <td style={td}>{p.counts.critical || "—"}</td>
                    <td style={td}>{n(p.devices)}</td>
                    <td style={{ ...td, color: p.daysToEndOfSupport != null
                                 && p.daysToEndOfSupport < 365 ? "var(--cares-amber)" : "var(--cares-text-note)" }}>
                      {p.endOfSupport
                        ? `${p.endOfSupport}${p.daysToEndOfSupport != null
                            ? ` (${p.daysToEndOfSupport}d)` : ""}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ ...card, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Sparkles size={15} style={{ color: "var(--cares-purple)" }} />
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, flex: 1 }}>
            Cyber Analyzer
          </h3>
          <button style={{ ...btn, background: "var(--cares-purple)", borderColor: "var(--cares-purple)",
                           color: "var(--cares-text-max)" }}
                  onClick={runAnalyzer} disabled={analysing}>
            {analysing ? <Loader2 size={13} /> : <Sparkles size={13} />}
            {analysing ? "Analysing…" : "Analyse the estate"}
          </button>
        </div>
        <p style={{ fontSize: 11.5, color: "var(--cares-text-label)", margin: "7px 0 0" }}>
          Reads the figures above — not the raw records — and is told to quote only
          what it is given, so every number in the briefing is traceable.
        </p>
        {analysisError && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8,
                        background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", border: "1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))",
                        fontSize: 11.5, color: "var(--cares-orange)" }}>
            {analysisError}
            {/expired|token|credential/i.test(analysisError) && (
              <div style={{ marginTop: 5, color: "var(--cares-orange)" }}>
                The AWS session credentials have lapsed. Everything else on this
                page works without them.
              </div>
            )}
          </div>
        )}
        {analysis && (
          <div style={{ marginTop: 12, background: "var(--cares-surface-board)", padding: "14px 18px",
                        borderRadius: 10, maxHeight: "56vh", overflowY: "auto",
                        border: "1px solid var(--cares-line-card)" }}>
            <Brief text={analysis.analysis} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- vulnerabilities */
function Vulnerabilities() {
  const [kevOnly, setKevOnly] = React.useState(false);
  const [severity, setSeverity] = React.useState("");
  const [q, setQ] = React.useState("");
  const [term, setTerm] = React.useState("");
  const { loading, data, error } = useAsync(
    () => api.getVulnerabilities({ kevOnly, severity: severity || undefined,
                                   q: term || undefined }),
    [kevOnly, severity, term]);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12,
                    alignItems: "center" }}>
        <div style={{ position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 9, top: 9,
                                     color: "var(--cares-text-note)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && setTerm(q)}
                 placeholder="product, CVE or weakness"
                 style={{ padding: "7px 10px 7px 27px", fontSize: 12, borderRadius: 8,
                          border: "1px solid var(--cares-line-strong)", width: 220 }} />
        </div>
        <button style={btn} onClick={() => setTerm(q)}>Search</button>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                style={{ ...btn, cursor: "pointer" }}>
          <option value="">All severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="CRITICAL,HIGH">Critical + High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <label style={{ ...btn, cursor: "pointer",
                        background: kevOnly ? "color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))" : "var(--cares-surface-card)",
                        borderColor: kevOnly ? "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))" : "var(--cares-line-strong)",
                        color: kevOnly ? "var(--cares-orange)" : "var(--cares-text-2)" }}>
          <input type="checkbox" checked={kevOnly}
                 onChange={(e) => setKevOnly(e.target.checked)}
                 style={{ marginRight: 2 }} />
          <Flame size={12} /> Known exploited only
        </label>
      </div>

      {loading ? <Loading /> : error ? <Err>{error}</Err> : (
        <>
          <Provenance provenance={data.provenance} compact />
          <div style={{ fontSize: 11.5, color: "var(--cares-text-label)", marginBottom: 8 }}>
            {data.count} matching · showing {data.returned}
          </div>
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>CVE</th><th style={th}>Severity</th>
                  <th style={th}>Product</th><th style={th}>Weakness</th>
                  <th style={th}>EPSS</th><th style={th}>Devices</th>
                  <th style={th}>Fix</th><th style={th}>KEV deadline</th>
                </tr></thead>
                <tbody>
                  {data.vulnerabilities.map((r) => (
                    <tr key={r.cveId + r.productId}
                        style={{ background: r.kevOverdue ? "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" : undefined }}>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: "ui-monospace, Menlo, monospace",
                                       fontSize: 11 }}>{r.cveId}</span>
                        {r.internetFacing && (
                          <div style={{ fontSize: 9.5, color: "var(--cares-amber)" }}>
                            internet-facing
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <Pill tone={SEV[r.severity]}>{r.severity} {r.cvss}</Pill>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>
                          {r.productName}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>{r.manufacturer}</div>
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>
                        {r.cweName}
                        <div style={{ fontSize: 9.5, color: "var(--cares-text-note)" }}>{r.cwe}</div>
                      </td>
                      <td style={td}>{(r.epss * 100).toFixed(1)}%</td>
                      <td style={td}>{n(r.devices)}</td>
                      <td style={td}>
                        {r.patchAvailable
                          ? <span style={{ color: "var(--cares-green)", fontSize: 11 }}>
                              {r.fixedVersion}
                            </span>
                          : <Pill tone={SEV.HIGH}>no patch</Pill>}
                      </td>
                      <td style={td}>
                        {r.knownExploited ? (
                          <span style={{ color: r.kevOverdue ? "var(--cares-orange)" : "var(--cares-amber)",
                                         fontWeight: 700, fontSize: 11 }}>
                            {r.kevOverdue
                              ? `overdue ${Math.abs(r.daysUntilKevDue)}d`
                              : `due in ${r.daysUntilKevDue}d`}
                            <div style={{ fontWeight: 400, fontSize: 9.5,
                                          color: "var(--cares-text-note)" }}>{r.kevDueDate}</div>
                          </span>
                        ) : <span style={{ color: "var(--cares-text-dim)" }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- lifecycle */
function Lifecycle() {
  const { loading, data, error } = useAsync(() => api.getLifecycle(), []);
  // Fetched alongside: the runway chart exists to put support expiry and
  // authorisation expiry on the same axis, and they come from different calls.
  const products = useAsync(() => api.getProducts(), []);
  const atos = useAsync(() => api.getAto(), []);
  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;

  return (
    <div>
      <Provenance provenance={data.provenance} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="Past end of support" value={data.pastEndOfSupport}
             tone={data.pastEndOfSupport ? "var(--cares-orange)" : "var(--cares-green)"}
             sub="no further security fixes" />
        <Kpi label="Products affected" value={data.count}
             sub={`within ${data.windowDays} days`} />
        <Kpi label="Devices affected" value={n(data.devicesAffected)} />
        <Kpi label="Spend affected" value={money(data.spendAffected)}
             sub="annual, on products losing support" />
      </div>

      {!products.loading && !atos.loading && products.data && atos.data && (
        <div style={{ ...card, padding: 16, marginBottom: 14 }}>
          <RunwayTimeline products={products.data.products} atos={atos.data.records} />
        </div>
      )}

      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={th}>Product</th><th style={th}>Version</th>
              <th style={th}>Support ends</th><th style={th}>Runway</th>
              <th style={th}>Devices</th><th style={th}>Annual spend</th>
              <th style={th}>Open CVEs</th><th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {data.products.map((r) => {
                const pct = Math.max(0, Math.min(100, (r.daysRemaining / 540) * 100));
                return (
                  <tr key={r.productId}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>{r.product}</div>
                      <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>
                        {r.manufacturer} · {r.category}
                      </div>
                    </td>
                    <td style={td}>{r.version || "—"}</td>
                    <td style={td}>{r.endOfSupport}</td>
                    <td style={{ ...td, minWidth: 130 }}>
                      <div style={{ fontSize: 11, fontWeight: 700,
                                    color: r.pastEndOfSupport ? "var(--cares-orange)"
                                         : r.daysRemaining <= 180 ? "var(--cares-amber)" : "var(--cares-text-3)" }}>
                        {r.pastEndOfSupport ? "unsupported" : `${r.daysRemaining} days`}
                      </div>
                      <div style={{ height: 5, background: "var(--cares-surface-board)", borderRadius: 3,
                                    marginTop: 3 }}>
                        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3,
                                      background: r.daysRemaining <= 180
                                        ? "var(--cares-orange)" : "var(--cares-green)" }} />
                      </div>
                    </td>
                    <td style={td}>{n(r.devices)}</td>
                    <td style={td}>{money(r.annualSpend)}</td>
                    <td style={td}>
                      {r.openVulnerabilities}
                      {r.knownExploited > 0 && (
                        <span style={{ color: "var(--cares-orange)", fontWeight: 700 }}>
                          {" "}({r.knownExploited} KEV)
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <Pill tone={r.pastEndOfSupport ? SEV.CRITICAL
                                  : r.daysRemaining <= 180 ? SEV.HIGH : SEV.MEDIUM}>
                        {r.action}
                      </Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ open source */
function OpenSource() {
  const [ecosystem, setEcosystem] = React.useState("");
  const [fixableOnly, setFixableOnly] = React.useState(false);
  const [directOnly, setDirectOnly] = React.useState(false);
  const { loading, data, error } = useAsync(
    () => api.getOpenSource({ ecosystem: ecosystem || undefined,
                              fixableOnly, directOnly }),
    [ecosystem, fixableOnly, directOnly]);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <select value={ecosystem} onChange={(e) => setEcosystem(e.target.value)}
                style={{ ...btn, cursor: "pointer" }}>
          <option value="">All ecosystems</option>
          {["npm", "PyPI", "Maven", "Go", "NuGet"].map((e) =>
            <option key={e} value={e}>{e}</option>)}
        </select>
        <label style={{ ...btn, cursor: "pointer" }}>
          <input type="checkbox" checked={fixableOnly}
                 onChange={(e) => setFixableOnly(e.target.checked)} />
          A fix exists
        </label>
        <label style={{ ...btn, cursor: "pointer" }}>
          <input type="checkbox" checked={directOnly}
                 onChange={(e) => setDirectOnly(e.target.checked)} />
          Direct dependencies only
        </label>
      </div>

      {loading ? <Loading /> : error ? <Err>{error}</Err> : (
        <>
          <Provenance provenance={data.provenance} compact />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <Kpi label="Advisories" value={data.count} />
            <Kpi label="Fix available" value={data.fixable} tone="var(--cares-green)"
                 sub={`${data.count - data.fixable} with no fix yet`} />
            <Kpi label="Transitive" value={data.transitive}
                 sub="pulled in by other packages" />
          </div>
          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={th}>Advisory</th><th style={th}>Severity</th>
                  <th style={th}>Package</th><th style={th}>Installed</th>
                  <th style={th}>Fixed in</th><th style={th}>Type</th>
                  <th style={th}>Found in product</th><th style={th}>Source</th>
                </tr></thead>
                <tbody>
                  {data.findings.map((r) => (
                    <tr key={r.ghsaId + r.productId}>
                      <td style={{ ...td, fontFamily: "ui-monospace, Menlo, monospace",
                                   fontSize: 10.5, whiteSpace: "nowrap" }}>
                        {r.ghsaId}
                        {r.cveId && (
                          <div style={{ color: "var(--cares-text-note)", fontSize: 9.5 }}>{r.cveId}</div>
                        )}
                      </td>
                      <td style={td}>
                        <Pill tone={SEV[r.severity]}>{r.severity} {r.cvss}</Pill>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: "var(--cares-text-1)", fontSize: 11.5 }}>
                          {r.package}
                        </div>
                        <div style={{ fontSize: 9.5, color: "var(--cares-text-note)" }}>{r.ecosystem}</div>
                      </td>
                      <td style={{ ...td, fontFamily: "ui-monospace, monospace",
                                   fontSize: 11 }}>{r.installedVersion}</td>
                      <td style={td}>
                        {r.fixAvailable
                          ? <span style={{ color: "var(--cares-green)", fontFamily: "ui-monospace, monospace",
                                           fontSize: 11 }}>{r.fixedVersion}</span>
                          : <Pill tone={SEV.HIGH}>none yet</Pill>}
                      </td>
                      <td style={td}>
                        <span style={{ fontSize: 10.5,
                                       color: r.dependencyType === "direct"
                                         ? "var(--cares-text-1)" : "var(--cares-text-note)" }}>
                          {r.dependencyType}
                        </span>
                      </td>
                      <td style={{ ...td, fontSize: 11 }}>{r.productName}</td>
                      <td style={td}>
                        <a href={`https://${r.repository}`} target="_blank"
                           rel="noreferrer"
                           style={{ display: "inline-flex", alignItems: "center", gap: 3,
                                    fontSize: 10.5, color: "var(--cares-purple)",
                                    textDecoration: "none" }}>
                          {r.source} <ExternalLink size={9} />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------------------------------------------- supply chain */
function SupplyChain() {
  const { loading, data, error } = useAsync(() => api.getSupplyChain(), []);
  if (loading) return <Loading />;
  if (error) return <Err>{error}</Err>;

  const { concentration: con, authorisation: auth, cscrmCoverage: cscrm } = data;
  const max = Math.max(...data.vendors.map((v) => v.spend), 1);

  return (
    <div>
      <Provenance provenance={data.provenance} compact />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="Vendors" value={data.count} />
        <Kpi label="Largest vendor share" value={`${con.topVendorShare}%`}
             sub={con.topVendor} />
        <Kpi label="Top three combined" value={`${con.topThreeShare}%`}
             tone={con.topThreeShare > 50 ? "var(--cares-amber)" : "var(--cares-text-1)"}
             sub="of annual spend" />
        <Kpi label="C-SCRM assessed" value={`${cscrm.assessed}/${cscrm.assessed + cscrm.unassessed}`}
             tone={cscrm.assessed === 0 ? "var(--cares-orange)" : "var(--cares-text-1)"}
             sub="supply-chain risk reviews on file" />
      </div>

      {cscrm.assessed === 0 && (
        <div style={{ ...card, background: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))", borderColor: "color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))", padding: 12,
                      marginBottom: 14, display: "flex", gap: 9, fontSize: 12,
                      color: "var(--cares-amber)" }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{cscrm.note}</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        <div style={{ ...card, flex: "2 1 420px", overflow: "hidden" }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, padding: "13px 14px 9px" }}>
            Spend concentration
          </h3>
          <div style={{ padding: "0 14px 14px" }}>
            {data.vendors.slice(0, 10).map((v) => (
              <div key={v.manufacturer} style={{ marginBottom: 9 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                              fontSize: 11.5, marginBottom: 3 }}>
                  <span style={{ fontWeight: 600, color: "var(--cares-text-1)" }}>
                    {v.manufacturer}
                    <span style={{ color: "var(--cares-text-note)", fontWeight: 400 }}>
                      {" "}· {v.products} product{v.products === 1 ? "" : "s"}
                      {v.kev > 0 && (
                        <span style={{ color: "var(--cares-orange)", fontWeight: 700 }}>
                          {" "}· {v.kev} exploited
                        </span>
                      )}
                    </span>
                  </span>
                  <span style={{ color: "var(--cares-text-3)" }}>
                    {money(v.spend)} ({v.spendShare}%)
                  </span>
                </div>
                <div style={{ height: 6, background: "var(--cares-surface-board)", borderRadius: 3 }}>
                  <div style={{ width: `${(v.spend / max) * 100}%`, height: "100%",
                                borderRadius: 3,
                                background: v.kev > 0 ? "var(--cares-orange)" : "var(--cares-purple)" }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...card, flex: "1 1 300px", overflow: "hidden" }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, padding: "13px 14px 9px" }}>
            Not FedRAMP authorised
          </h3>
          <div style={{ padding: "0 14px 14px" }}>
            {auth.notAuthorized.length === 0 ? (
              <p style={{ fontSize: 12, color: "var(--cares-green)" }}>
                Every product carries an authorisation.
              </p>
            ) : auth.notAuthorized.map((p) => (
              <div key={p.product} style={{ padding: "8px 0",
                                            borderBottom: "1px solid var(--cares-line)" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cares-text-1)" }}>
                  {p.product}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--cares-text-note)" }}>
                  {p.manufacturer} · {n(p.devices)} devices · {money(p.spend)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ shell */
export default function CyberPage({ initialTab = "posture" }) {
  const [tab, setTab] = React.useState(initialTab);
  React.useEffect(() => { setTab(initialTab); }, [initialTab]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cares-text-1)", margin: 0,
                     display: "flex", alignItems: "center", gap: 9 }}>
          <ShieldAlert size={20} style={{ color: "var(--cares-orange)" }} /> Cyber
        </h1>
        <p style={{ fontSize: 13, color: "var(--cares-text-label)", margin: "6px 0 0", maxWidth: 720 }}>
          Vulnerability, lifecycle and supply-chain exposure across the technology
          estate — what is exploited today, what stops being patched, and what is
          built on components nobody has looked at.
        </p>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--cares-line-card)",
                    marginBottom: 16, flexWrap: "wrap" }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             padding: "9px 13px", background: "none", border: "none",
                             cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                             color: on ? "var(--cares-purple)" : "var(--cares-text-label)",
                             borderBottom: on ? "2px solid var(--cares-purple)"
                                              : "2px solid transparent" }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "posture" && <Posture />}
      {tab === "riskmap" && <RiskMap />}
      {tab === "vulnerabilities" && <Vulnerabilities />}
      {tab === "lifecycle" && <Lifecycle />}
      {tab === "opensource" && <OpenSource />}
      {tab === "supply" && <SupplyChain />}
      {tab === "sbom" && <Sbom />}
      {tab === "authorisation" && <Authorisation />}
      {tab === "ai" && <AiSystems />}
      {tab === "trends" && <Trends />}
    </div>
  );
}
